/**
 * Lesson timer — pure state derivation. No React, no Firestore, no Date.now():
 * callers pass `nowMs` so every state is reproducible in a test.
 *
 * THE TIMER COUNTS DOWN TO THE SCHEDULED END. IT IS NOT A STOPWATCH.
 * (Changed 2026-09-02 on Anthony's review, card bChLhzvkSLDTCwypHxRP.)
 *
 * It used to derive everything from `startedAt`, so a teacher who forgot to tap
 * the pill got no clock at all and, worse, a wrong record. That is not
 * hypothetical: the 19:00 lesson on 2026-09-02 was started at 19:14:31 and
 * completed at 19:50, so the stopwatch reported 35 minutes for a 45-minute
 * lesson. The schedule is the thing that is actually true — the student is in
 * the room at 7:00 whether or not anyone pressed a button — so the schedule is
 * the anchor, and the clock runs on its own.
 *
 * `startedAt` is still written and still matters, but its job changed: it is
 * now the record of the ACTUAL start, which is exactly what makes the
 * running-behind figure available (`lateStartMs = startedAt − scheduledStart`).
 * That is the number Anthony asked to be able to carry through the day.
 *
 * **THE CLOCK ADVANCING IS NOT EVIDENCE THAT A LESSON HAPPENED.** Under the old
 * stopwatch, pressing start was a human assertion that the lesson was running.
 * It is not any more. Nothing may infer attendance from timer state — attendance
 * comes only from the explicit outcome control and from `complete()`. See
 * `systems.md` → Attendance, and the pin in lessonTimerModel.test.js.
 *
 * The pill is a PILL in the topbar, never a card (design handoff 2026-07-22).
 * Hue reinforces; the label carries the meaning.
 *
 *   upcoming     before the scheduled start · "Starts in 12:00", tap starts early
 *   running      quiet success hue · time LEFT, "32:15 left"
 *   ending_soon  ≤5 minutes left · warning hue
 *   overrun      past the scheduled end · danger hue, "5:12 over".
 *                Informational, never blocks — lessons run long, the pill just
 *                tells the truth.
 *   saved        on Complete · check icon, muted, actual total "35:34"
 *   past         an hour after the scheduled end with no Complete. The
 *                clock STOPS and the pill says where the record stands
 *                ("Attended · not completed", "Attendance not marked").
 *                Before 2026-09-29 the overrun counted up forever, so a
 *                lesson opened days later read "26:14:03 over", which said
 *                nothing true and looked like a lesson still running.
 *   idle         ONLY when the lesson carries no usable date/time, so no
 *                countdown is possible. Falls back to the old stopwatch rather
 *                than inventing a schedule. See deriveStopwatchState.
 */

export const TIMER_STATE = Object.freeze({
	UPCOMING: 'upcoming',
	IDLE: 'idle',
	RUNNING: 'running',
	ENDING_SOON: 'ending_soon',
	OVERRUN: 'overrun',
	SAVED: 'saved',
	PAST: 'past',
});

/** Minutes-left threshold at which running turns to ending_soon. */
export const ENDING_SOON_MS = 5 * 60 * 1000;

/** Default lesson length when the doc carries none. */
export const DEFAULT_DURATION_MINUTES = 45;

/** How long past the scheduled end a lesson stays "overrun" before it is
 *  treated as over. Lessons run long by minutes, never by an hour. */
export const PAST_GRACE_MS = 60 * 60 * 1000;

/** The pill label for a lesson that is over but was never completed. Reads
 *  the attendance outcome, because that is the record a teacher can act on. */
const PAST_LABEL = {
	attended: 'Attended · not completed',
	no_show: 'No-show',
	cancelled_family: 'Cancelled',
	cancelled_cyv: 'Cancelled',
};

/** A start is only "late" once it is at least this far behind schedule —
 *  below a minute it is noise, not information a teacher should act on. */
export const LATE_START_FLOOR_MS = 60 * 1000;

/**
 * Firestore Timestamp | Date | epoch-ms | null → epoch ms or null.
 */
export const toEpochMs = (value) => {
	if (value == null) return null;
	if (typeof value === 'number') return value;
	if (typeof value.toDate === 'function') return value.toDate().getTime();
	if (typeof value.seconds === 'number') return value.seconds * 1000;
	if (value instanceof Date) return value.getTime();
	return null;
};

/** ms → "m:ss" with total minutes (33:05, not 0:33:05 — matches the mockups). */
export const formatClockMs = (ms) => {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** "16:30" (24h) → "4:30". No am/pm: the pill is glanced at mid-afternoon by
 *  someone who knows which half of the day it is. */
export const formatScheduledShort = (time24) => {
	if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return '';
	const [h, m] = time24.split(':');
	const hour = parseInt(h, 10);
	if (Number.isNaN(hour)) return '';
	const hour12 = hour % 12 || 12;
	return `${hour12}:${m}`;
};

/** epoch ms → "7:53", for the "full length would end at" readout. */
export const formatEpochShort = (ms) => {
	if (ms == null) return '';
	const d = new Date(ms);
	const hour12 = d.getHours() % 12 || 12;
	return `${hour12}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Whole minutes, rounded down, for prose ("14 min late"). */
export const minutesOf = (ms) => Math.floor(Math.max(0, ms) / 60000);

/**
 * The lesson's scheduled start as an epoch instant.
 *
 * TIMEZONE, AND WHY THE LOCAL CONSTRUCTOR IS RIGHT *HERE* SPECIFICALLY.
 * `lesson.date` is a calendar-day string ("2026-09-02") and `lesson.time` a
 * wall-clock string ("19:00"), both studio-local. CLAUDE.md carries two rules
 * that pull in opposite directions and it is worth being explicit about which
 * applies:
 *
 *  - never `new Date("2026-09-02")` — that parses as UTC midnight and lands on
 *    the previous evening in every US timezone. Obeyed: we split the parts.
 *  - a *deployed Cloud Function* must pin construction to America/Chicago,
 *    because its process runs in UTC and the local constructor is then the
 *    same trap wearing the fix's clothes.
 *
 * This file runs in the TEACHER'S BROWSER, in the studio's own timezone, so
 * "local" already means America/Chicago and the local constructor is correct.
 * If this derivation is ever moved server-side, it must switch to the
 * `triageDateFromKey` idiom in functions/triageApply.js — do not port it as-is.
 *
 * @returns {number|null} null when either part is missing or malformed, which
 *   is what puts the pill into its stopwatch fallback rather than guessing.
 */
export const scheduledStartMs = (dateStr, time24) => {
	if (typeof dateStr !== 'string' || typeof time24 !== 'string') return null;
	const dm = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	const tm = time24.match(/^(\d{1,2}):(\d{2})$/);
	if (!dm || !tm) return null;
	const [, y, mo, d] = dm.map(Number);
	const [, hh, mi] = tm.map(Number);
	if (hh > 23 || mi > 59) return null;
	const dt = new Date(y, mo - 1, d, hh, mi, 0, 0);
	// Reject a rolled-over date (e.g. month 13, day 32 sneaking past the regex).
	if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
	return dt.getTime();
};

/**
 * The pre-2026-09-02 behaviour, kept for lessons with no usable schedule.
 * Elapsed-since-start, exactly as before — an honest stopwatch is better than
 * a countdown to a time we do not know.
 */
const deriveStopwatchState = ({ startedMs, durationMs, nowMs }) => {
	if (startedMs == null) {
		return {
			state: TIMER_STATE.IDLE,
			label: 'Start timer',
			note: '',
			elapsedMs: 0,
			remainingMs: durationMs,
			overageMs: 0,
		};
	}
	const elapsedMs = Math.max(0, nowMs - startedMs);
	const remainingMs = durationMs - elapsedMs;
	if (remainingMs <= 0) {
		const overageMs = -remainingMs;
		return {
			state: TIMER_STATE.OVERRUN,
			label: `${formatClockMs(overageMs)} over`,
			note: '',
			elapsedMs,
			remainingMs: 0,
			overageMs,
		};
	}
	return {
		state: remainingMs <= ENDING_SOON_MS ? TIMER_STATE.ENDING_SOON : TIMER_STATE.RUNNING,
		label: `${formatClockMs(remainingMs)} left`,
		note: '',
		elapsedMs,
		remainingMs,
		overageMs: 0,
	};
};

/**
 * Derive the pill state.
 *
 * @param {object} args
 * @param {*}      args.startedAt   lesson.startedAt — the ACTUAL start, or null
 * @param {*}      args.completedAt lesson.completedAt
 * @param {string} args.status      'completed' wins even if completedAt failed
 * @param {string} [args.outcome]   lesson.outcome, read only once the lesson is past
 * @param {number} args.durationMinutes lesson.duration
 * @param {string} args.scheduledDate   lesson.date, "2026-09-02"
 * @param {string} args.scheduledTime   lesson.time, 24h "19:00"
 * @param {number} args.nowMs        current epoch ms — REQUIRED
 * @returns {{state, label, note, elapsedMs, remainingMs, overageMs,
 *            lateStartMs, scheduledStart, scheduledEnd, fullLengthEnd, isCountdown}}
 *   `note` is the secondary line: the running-behind figure and the full-length
 *   end time. It is ALWAYS RENDERED when non-empty, never hover-revealed —
 *   keratoconus, and the installed PWA has no hover at all (CLAUDE.md).
 */
export const deriveTimerState = ({
	startedAt,
	completedAt,
	status,
	outcome,
	durationMinutes,
	scheduledDate,
	scheduledTime,
	nowMs,
}) => {
	const startedMs = toEpochMs(startedAt);
	const completedMs = toEpochMs(completedAt);
	const durationMs = (durationMinutes || DEFAULT_DURATION_MINUTES) * 60 * 1000;
	const schedStart = scheduledStartMs(scheduledDate, scheduledTime);
	const schedEnd = schedStart == null ? null : schedStart + durationMs;

	const lateStartMs =
		schedStart != null && startedMs != null ? Math.max(0, startedMs - schedStart) : 0;
	const fullLengthEnd = startedMs != null ? startedMs + durationMs : null;

	const shared = {
		lateStartMs,
		scheduledStart: schedStart,
		scheduledEnd: schedEnd,
		fullLengthEnd,
		isCountdown: schedEnd != null,
		// Has the ACTUAL start been recorded? The pill keys its tappability on
		// this rather than on a state name: under the countdown, "the clock is
		// running" and "someone told us when it began" are different facts.
		startedRecorded: startedMs != null,
	};

	if (completedMs != null || status === 'completed') {
		// Saved. Total = completed − started; if either edge is missing there is
		// no honest number, so show the check with no time rather than a lie.
		const elapsedMs =
			completedMs != null && startedMs != null ? Math.max(0, completedMs - startedMs) : null;
		return {
			...shared,
			state: TIMER_STATE.SAVED,
			label: elapsedMs != null ? formatClockMs(elapsedMs) : 'Saved',
			note:
				lateStartMs >= LATE_START_FLOOR_MS
					? `Started ${minutesOf(lateStartMs)} min late`
					: '',
			elapsedMs: elapsedMs ?? 0,
			remainingMs: 0,
			overageMs: 0,
		};
	}

	if (schedEnd == null) {
		return { ...shared, ...deriveStopwatchState({ startedMs, durationMs, nowMs }) };
	}

	// ---- Over and never completed. The clock stops here: nothing is running,
	// and the pill's job becomes saying what is (and is not) on the record.
	if (nowMs >= schedEnd + PAST_GRACE_MS) {
		return {
			...shared,
			state: TIMER_STATE.PAST,
			label: PAST_LABEL[outcome] || 'Attendance not marked',
			needsAttendance: !PAST_LABEL[outcome],
			note: '',
			elapsedMs: 0,
			remainingMs: 0,
			overageMs: 0,
		};
	}

	// ---- Countdown proper. The clock runs whether or not anyone pressed start.

	// Before the lesson is due to begin.
	if (nowMs < schedStart) {
		return {
			...shared,
			state: TIMER_STATE.UPCOMING,
			label: `Starts in ${formatClockMs(schedStart - nowMs)}`,
			note: '',
			elapsedMs: 0,
			remainingMs: durationMs,
			overageMs: 0,
		};
	}

	const remainingMs = schedEnd - nowMs;
	const elapsedMs = startedMs != null ? Math.max(0, nowMs - startedMs) : Math.max(0, nowMs - schedStart);

	/**
	 * The secondary line. Two numbers, deliberately, because Anthony's ask was
	 * that a teacher running behind can CHOOSE — go over and carry the delay
	 * through the day, or end on schedule and eat the time. Showing only one of
	 * them makes that choice for them.
	 */
	let note = '';
	if (startedMs == null) {
		// Nobody has pressed start and the lesson is due. Say so plainly rather
		// than silently pretending it began on time.
		const inMin = minutesOf(nowMs - schedStart);
		note = inMin >= 1 ? `Not started · ${inMin} min in` : 'Not started';
	} else if (lateStartMs >= LATE_START_FLOOR_MS) {
		note = `Started ${minutesOf(lateStartMs)} min late · full ${
			durationMinutes || DEFAULT_DURATION_MINUTES
		} min ends ${formatEpochShort(fullLengthEnd)}`;
	}

	if (remainingMs <= 0) {
		const overageMs = -remainingMs;
		return {
			...shared,
			state: TIMER_STATE.OVERRUN,
			label: `${formatClockMs(overageMs)} over`,
			note: note || `Scheduled end was ${formatEpochShort(schedEnd)}`,
			elapsedMs,
			remainingMs: 0,
			overageMs,
		};
	}

	return {
		...shared,
		state: remainingMs <= ENDING_SOON_MS ? TIMER_STATE.ENDING_SOON : TIMER_STATE.RUNNING,
		label: `${formatClockMs(remainingMs)} left`,
		note,
		elapsedMs,
		remainingMs,
		overageMs: 0,
	};
};
